/** Runtime character removal — holiday / lunch / forever (notes §12). */

export type AbsenceReason = 'holiday' | 'lunch' | 'forever';

export interface CharacterAbsenceRecord {
  characterId: string;
  firstName: string;
  reason: AbsenceReason;
  removedAt: string;
  returnAt: string | null;
  undoneAt?: string;
}

const HOLIDAY_MS = 14 * 24 * 60 * 60 * 1000;
const LUNCH_MS = 60 * 60 * 1000;
const HISTORY_MS = 183 * 24 * 60 * 60 * 1000;

export function absenceReturnAt(reason: AbsenceReason, removedAt: string): string | null {
  const base = new Date(removedAt).getTime();
  if (reason === 'holiday') return new Date(base + HOLIDAY_MS).toISOString();
  if (reason === 'lunch') return new Date(base + LUNCH_MS).toISOString();
  return null;
}

export function isAbsenceActive(record: CharacterAbsenceRecord, nowIso: string): boolean {
  if (record.undoneAt) return false;
  if (!record.returnAt) return true;
  return new Date(nowIso).getTime() < new Date(record.returnAt).getTime();
}

export function pruneAbsenceHistory(
  records: CharacterAbsenceRecord[],
  nowIso: string,
): CharacterAbsenceRecord[] {
  const cutoff = new Date(nowIso).getTime() - HISTORY_MS;
  return records.filter((r) => new Date(r.removedAt).getTime() >= cutoff);
}

export function createAbsenceRecord(args: {
  characterId: string;
  firstName: string;
  reason: AbsenceReason;
  removedAt: string;
}): CharacterAbsenceRecord {
  return {
    characterId: args.characterId,
    firstName: args.firstName,
    reason: args.reason,
    removedAt: args.removedAt,
    returnAt: absenceReturnAt(args.reason, args.removedAt),
  };
}

export function undoAbsence(
  records: CharacterAbsenceRecord[],
  characterId: string,
  undoneAt: string,
): CharacterAbsenceRecord[] {
  return records.map((r) =>
    r.characterId === characterId && !r.undoneAt ? { ...r, undoneAt } : r,
  );
}

export function activeAbsentCharacterIds(
  records: CharacterAbsenceRecord[],
  nowIso: string,
): Set<string> {
  const ids = new Set<string>();
  for (const r of records) {
    if (isAbsenceActive(r, nowIso)) ids.add(r.characterId);
  }
  return ids;
}
